/**
 * @bos/portal3 — the industry / business-type picker (M6).
 *
 * A curated dropdown of common small-business types plus an "Other…" option that reveals
 * a free-text field, so an owner is never boxed out of naming their own trade. The value
 * handed up is always the plain industry string the server stores (or '' for none) — the
 * list vs. free-text distinction is a presentation detail kept entirely inside here.
 */
import { useState } from 'react';
import { INDUSTRY_OPTIONS, INDUSTRY_OTHER } from './industries';

export function IndustryField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const isKnown = (INDUSTRY_OPTIONS as readonly string[]).includes(value);
  // Seeded once from the incoming value: a non-empty value that isn't a listed option is
  // a custom ("Other") entry. Remounting via `key` (on project change) re-seeds cleanly.
  const [isOther, setIsOther] = useState(value !== '' && !isKnown);

  const selectValue = isOther ? INDUSTRY_OTHER : value;

  function onSelect(next: string) {
    if (next === INDUSTRY_OTHER) {
      setIsOther(true);
      onChange(''); // start the free-text field empty
    } else {
      setIsOther(false);
      onChange(next);
    }
  }

  return (
    <div className="form__field">
      <label className="form__label" htmlFor="project-industry">
        Industry
      </label>
      <select
        id="project-industry"
        name="industry"
        className="form__select"
        value={selectValue}
        onChange={(e) => onSelect(e.target.value)}
        disabled={disabled}
      >
        <option value="">Choose a type…</option>
        {INDUSTRY_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
        <option value={INDUSTRY_OTHER}>Other…</option>
      </select>
      {isOther && (
        <input
          className="form__input"
          type="text"
          name="industryOther"
          aria-label="Industry (other)"
          placeholder="Describe your business type"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )}
    </div>
  );
}
